import { NEW_SUBMIT, type SubmitForm } from './bindingSubmitModel';
import { useState } from 'react';
import type { App, ExecutionMode, Platform, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { useFeature } from '@/lib/permissions';
import {
  activeEnvironments,
  addAssertion,
  allowedModesFor,
  initialProfileForm,
  isBitrise,
  normalizeAssertionKey,
  patchAssertion,
  patchBinding,
  removeAssertion,
  selectableAccounts,
  toggleMode,
  withAccount,
  withPlatform,
  type ProfileFormValues,
} from './profileFormModel';
import { validateProfileForm, type LocalField } from './profileFormValidation';
import { useProfileSubmit, type FormFeedback } from './useProfileSubmit';

export interface ProfileFormProps {
  app: App;
  project: Project;
  /** Edit this profile (slug and platform stay). */
  editing?: Profile;
  /** Create a new profile prefilled from this one ("Duplicate"). */
  duplicateOf?: Profile;
  accounts: ProviderAccount[] | undefined;
  onDone: () => void;
}

/** Feedback state: whether the user tried to submit, the server's answer, and whether "Advanced" is open. */
function useFormFeedback() {
  const [advanced, setAdvanced] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const [server, setServer] = useState<Record<string, string>>({});
  /** Blanks one server error as soon as the user edits the field it was about. */
  const clearServer = (key: string) => {
    if (server[key]) setServer((s) => ({ ...s, [key]: '' }));
  };
  const feedback: FormFeedback = { setSubmitted, setProblem, setServer, setAdvanced };
  return { advanced, submitted, problem, server, clearServer, feedback };
}

/** The form values plus what is derived from the props: environments, selectable accounts, the picked account. */
function useProfileValues({ project, editing, duplicateOf, accounts }: ProfileFormProps) {
  const bitriseFeature = useFeature('bitrise');
  const runnerOff = useFeature('runner') === false;
  const environments = activeEnvironments(project.environments);
  const selectable = selectableAccounts(accounts, bitriseFeature, (editing ?? duplicateOf)?.providerAccountId);
  const [values, setValues] = useState<ProfileFormValues>(() => initialProfileForm({ editing, duplicateOf, accounts, selectable, environments }));
  const account = accounts?.find((a) => a.id === values.providerAccountId);
  const patch = (p: Partial<ProfileFormValues>) => setValues((v) => ({ ...v, ...p }));
  return { values, setValues, patch, environments, selectable, account, runnerOff, allowedModes: allowedModesFor(account?.kind), bitriseProfile: isBitrise(account?.kind) };
}

type ValuesState = ReturnType<typeof useProfileValues>;

/** The handlers the sections call: each updates the values and, where the field had a server error, clears it. */
function useProfileHandlers({ setValues, patch, accounts }: Pick<ValuesState, 'setValues' | 'patch'> & { accounts: ProviderAccount[] | undefined }, clearServer: (key: string) => void) {
  const setAssertions = (f: (a: ProfileFormValues['assertions']) => ProfileFormValues['assertions']) => setValues((v) => ({ ...v, assertions: f(v.assertions) }));
  return {
    setSlug: (slug: string) => {
      patch({ slug: slug.toLowerCase() });
      clearServer('slug');
    },
    setPlatform: (pl: Platform) => setValues((v) => withPlatform(v, pl)),
    pickAccount: (id: string) => setValues((v) => withAccount(v, id, accounts)),
    setMode: (m: ExecutionMode, on: boolean) => setValues((v) => ({ ...v, modes: toggleMode(v.modes, m, on) })),
    setProviderProfile: (providerProfile: string) => {
      patch({ providerProfile });
      clearServer('providerProfile');
    },
    setBindingOn: (envId: string, on: boolean) => setValues((v) => ({ ...v, bindings: patchBinding(v.bindings, envId, { on }) })),
    setBindingProfile: (envId: string, profile: string) => {
      setValues((v) => ({ ...v, bindings: patchBinding(v.bindings, envId, { profile }) }));
      clearServer(`binding:${envId}`);
    },
    /** The store app an environment submits to; none also turns its submission off. */
    setBindingStoreApp: (envId: string, storeAppId: string) => {
      setValues((v) => ({ ...v, bindings: patchBinding(v.bindings, envId, storeAppId ? { storeAppId } : { storeAppId: '', submit: null }) }));
      clearServer(`binding:${envId}`);
    },
    /** Turns an environment's submission on (from the defaults), changes it, or turns it off (null). */
    setBindingSubmit: (envId: string, change: Partial<SubmitForm> | null) => {
      setValues((v) => ({ ...v, bindings: patchBinding(v.bindings, envId, { submit: change === null ? null : { ...(v.bindings[envId]?.submit ?? NEW_SUBMIT), ...change } }) }));
      clearServer(`binding:${envId}`);
    },
    addAssertion: () => setAssertions(addAssertion),
    setAssertionKey: (i: number, key: string) => setAssertions((a) => patchAssertion(a, i, { key: normalizeAssertionKey(key) })),
    setAssertionPattern: (i: number, pattern: string) => setAssertions((a) => patchAssertion(a, i, { pattern })),
    removeAssertion: (i: number) => setAssertions((a) => removeAssertion(a, i)),
  };
}

/** State, validation, handlers and submit of the build-profile form. The sections render what this returns. */
export function useProfileForm(props: ProfileFormProps) {
  const state = useProfileValues(props);
  const ui = useFormFeedback();
  const { values, environments, account } = state;
  const validation = validateProfileForm(values, { accountKind: account?.kind, environments });
  const handlers = useProfileHandlers({ ...state, accounts: props.accounts }, ui.clearServer);
  const { onSubmit, pending } = useProfileSubmit({ editing: props.editing, onDone: props.onDone, appId: props.app.id, values, validation, environments, account, allowedModes: state.allowedModes, feedback: ui.feedback });
  /** The message to show under a field: the server's, else the local one once submitted (or `live` while typing). */
  const errorFor = (k: LocalField, live = false) => ui.server[k] || (ui.submitted || live ? validation.local[k] : undefined);
  const bindingError = (envId: string) => ui.server[`binding:${envId}`] || validation.bindingErrors[envId];
  return { ...state, ...handlers, ...ui, appId: props.app.id, edit: Boolean(props.editing), errorFor, bindingError, onSubmit, pending, setAdvanced: ui.feedback.setAdvanced };
}

export type ProfileFormApi = ReturnType<typeof useProfileForm>;
